/**
 * =============================================================================
 * components.js — We Are Rewind
 * =============================================================================
 * Remplace webflow.js et jQuery. Reproduit, en JS natif et à l'identique, les
 * modules Webflow dont le thème se sert encore :
 *   - dropdown (.w-dropdown) : mini-panier, recherche, sélecteur langue/pays,
 *     menus de navigation (au survol) ;
 *   - navbar (.w-nav) : menu mobile/tablette ;
 *   - links : classe .w--current sur les liens de la page courante (et
 *     scroll-spy des ancres de la page) ;
 *   - scroll : les liens « # » ne font pas remonter la page ; défilement doux
 *     vers les ancres de la page.
 *
 * Modules Webflow non repris car sans effet sur ce thème : brand (badge
 * « Made in Webflow », jamais affiché), focus (inactif sans
 * body[data-wf-focus-within]), focus-visible (polyfill, :focus-visible est
 * natif), touch (gestes des sliders Webflow), forms (tous les formulaires du
 * thème ont une action : soumission native), tabs (plus utilisé), ix2/ix3
 * (voir animations.js).
 *
 * Comme webflow.js, les dropdowns et la navbar émettent COMPONENT_ACTIVE /
 * COMPONENT_INACTIVE (CustomEvent qui remonte) à l'ouverture / fermeture :
 * animations.js s'en sert pour animer les dropdowns.
 * Détail et tests : docs/webflow-removal.md.
 * =============================================================================
 */
(function () {
  'use strict';

  const doc = document;
  const touch = 'ontouchstart' in window || (window.DocumentTouch && doc instanceof window.DocumentTouch);

  const KEY = { ENTER: 13, ESCAPE: 27, SPACE: 32, END: 35, HOME: 36, LEFT: 37, UP: 38, RIGHT: 39, DOWN: 40 };
  const KEY_NAMES = {
    Enter: 13, Escape: 27, Esc: 27, ' ': 32, Spacebar: 32, End: 35, Home: 36,
    ArrowLeft: 37, Left: 37, ArrowUp: 38, Up: 38, ArrowRight: 39, Right: 39, ArrowDown: 40, Down: 40,
  };
  // LiquifyHelper.triggerClick() simule « Entrée » avec keyCode ; les vrais claviers donnent key.
  const keyOf = (e) => e.keyCode || KEY_NAMES[e.key] || 0;

  // Webflow passait ses gestionnaires par un debounce sans délai : ils s'exécutent
  // au tick suivant, et plusieurs appels dans le même tick n'en font qu'un.
  function deferred(fn) {
    let timer = 0;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), 0);
    };
  }

  const emit = (el, type) => el.dispatchEvent(new CustomEvent(type, { bubbles: true, cancelable: true }));
  const childrenWith = (el, cls) => Array.from(el.children).filter((c) => c.classList.contains(cls));
  const px = (v) => parseFloat(v) || 0;

  // Mesures équivalentes à jQuery .height() et .outerHeight(true) / .outerWidth(true)
  // (vérifiées contre jQuery sur le site live, éléments affichés).
  function height(el) {
    const cs = getComputedStyle(el);
    return (
      el.getBoundingClientRect().height - px(cs.paddingTop) - px(cs.paddingBottom) -
      px(cs.borderTopWidth) - px(cs.borderBottomWidth)
    );
  }
  function outerHeight(el) {
    const cs = getComputedStyle(el);
    return el.getBoundingClientRect().height + px(cs.marginTop) + px(cs.marginBottom);
  }
  function outerWidth(el) {
    const cs = getComputedStyle(el);
    return el.getBoundingClientRect().width + px(cs.marginLeft) + px(cs.marginRight);
  }

  // Sur écran tactile, Webflow ignorait les « clics fantômes » (clic dont la cible
  // n'est pas celle du dernier touchstart/mousedown).
  let lastPointerTarget = null;
  if (touch) {
    doc.addEventListener('touchstart', (e) => (lastPointerTarget = e.target));
    doc.addEventListener('mousedown', (e) => (lastPointerTarget = e.target));
  }
  const validClick = (el) => !touch || el === lastPointerTarget || el.contains(lastPointerTarget);

  /* ===========================================================================
   * Dropdown (.w-dropdown)
   * ======================================================================== */
  const OPEN = 'w--open';
  const dropdowns = [];
  const pointerEvent = touch ? 'click' : 'mouseup';

  function openDropdown(dd) {
    if (dd.open) return;
    // Un dropdown qui s'ouvre ferme les autres (sauf ceux qui le contiennent).
    dropdowns.forEach((other) => {
      if (other !== dd && !other.el.contains(dd.el)) other.requestClose();
    });
    dd.open = true;
    dd.lists.forEach((l) => l.classList.add(OPEN));
    dd.toggles.forEach((t) => {
      t.classList.add(OPEN);
      t.setAttribute('aria-expanded', 'true');
    });
    emit(dd.el, 'COMPONENT_ACTIVE');
    if (dd.manageZ) dd.el.style.zIndex = 901;
    doc.addEventListener(pointerEvent, dd.onPointerOutside);
    if (dd.hovering) dd.el.addEventListener('mouseleave', dd.onMouseLeave);
    clearTimeout(dd.delayId);
  }

  function closeDropdown(dd, { immediate, forceClose } = {}) {
    if (!dd.open || (dd.hover && dd.hovering && !forceClose)) return;
    dd.toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    dd.open = false;
    emit(dd.el, 'COMPONENT_INACTIVE');
    doc.removeEventListener(pointerEvent, dd.onPointerOutside);
    dd.el.removeEventListener('mouseleave', dd.onMouseLeave);
    clearTimeout(dd.delayId);
    // data-delay est lu comme une chaîne : « 0 » diffère quand même d'un tick.
    if (!dd.delay || immediate) dd.complete();
    else dd.delayId = setTimeout(dd.complete, Number(dd.delay));
  }

  function focusLink(dd) {
    const link = dd.links[dd.selectedIdx];
    if (link) link.focus();
  }

  function setupDropdown(el, index) {
    const dd = { el, open: false, hovering: false, selectedIdx: -1, delayId: 0 };
    dd.toggles = childrenWith(el, 'w-dropdown-toggle');
    dd.lists = childrenWith(el, 'w-dropdown-list');
    dd.links = dd.lists
      .flatMap((l) => Array.from(l.querySelectorAll('a')))
      .filter((a) => !a.matches('.w-dropdown .w-dropdown a'));

    const zIndex = Number(getComputedStyle(el).zIndex);
    dd.manageZ = zIndex === 900 || zIndex === 901;
    dd.hover = el.getAttribute('data-hover') === 'true' && !touch;
    dd.delay = el.getAttribute('data-delay');

    dd.complete = () => {
      dd.lists.forEach((l) => l.classList.remove(OPEN));
      dd.toggles.forEach((t) => t.classList.remove(OPEN));
      if (dd.manageZ) dd.el.style.zIndex = '';
    };
    dd.toggleOpen = deferred(() => (dd.open ? closeDropdown(dd, { forceClose: true }) : openDropdown(dd)));
    dd.toggleByKey = deferred(() => (dd.open ? closeDropdown(dd, { forceClose: true }) : openDropdown(dd)));
    dd.requestClose = deferred(() => closeDropdown(dd, { forceClose: true }));
    dd.onPointerOutside = deferred((e) => {
      if (!dd.open) return;
      const target = e.target;
      if (!(target instanceof Element) || target.closest('.w-dropdown-toggle')) return;
      const inside = dd.el !== target && dd.el.contains(target);
      if (!inside) closeDropdown(dd);
    });
    dd.onMouseLeave = () => {
      dd.hovering = false;
      if (!dd.links.includes(doc.activeElement)) closeDropdown(dd);
    };

    // Accessibilité (identique à Webflow)
    const toggle = dd.toggles[0];
    const list = dd.lists[0];
    const toggleId = (toggle && toggle.id) || 'w-dropdown-toggle-' + index;
    const listId = (list && list.id) || 'w-dropdown-list-' + index;
    dd.toggles.forEach((t) => {
      t.id = toggleId;
      t.setAttribute('aria-controls', listId);
      t.setAttribute('aria-haspopup', 'menu');
      t.setAttribute('aria-expanded', 'false');
      t.querySelectorAll('.w-icon-dropdown-toggle').forEach((i) => i.setAttribute('aria-hidden', 'true'));
      if (t.tagName !== 'BUTTON') {
        t.setAttribute('role', 'button');
        if (!t.hasAttribute('tabindex')) t.setAttribute('tabindex', '0');
      }
    });
    dd.lists.forEach((l) => {
      l.id = listId;
      l.setAttribute('aria-labelledby', toggleId);
    });
    dd.links.forEach((a) => {
      if (!a.hasAttribute('tabindex')) a.setAttribute('tabindex', '0');
      if (/^#[a-zA-Z0-9\-_]+$/.test(a.hash)) a.addEventListener('click', () => closeDropdown(dd));
    });

    if (dd.hover) {
      dd.toggles.forEach((t) =>
        t.addEventListener('mouseenter', () => {
          dd.hovering = true;
          openDropdown(dd);
        })
      );
    }

    el.addEventListener('keydown', (e) => {
      if (!dd.open) return;
      dd.selectedIdx = dd.links.indexOf(doc.activeElement);
      switch (keyOf(e)) {
        case KEY.HOME:
          dd.selectedIdx = 0;
          focusLink(dd);
          e.preventDefault();
          return;
        case KEY.END:
          dd.selectedIdx = dd.links.length - 1;
          focusLink(dd);
          e.preventDefault();
          return;
        case KEY.ESCAPE:
          closeDropdown(dd);
          if (toggle) toggle.focus();
          e.stopPropagation();
          return;
        case KEY.RIGHT:
        case KEY.DOWN:
          dd.selectedIdx = Math.min(dd.links.length - 1, dd.selectedIdx + 1);
          focusLink(dd);
          e.preventDefault();
          return;
        case KEY.LEFT:
        case KEY.UP:
          dd.selectedIdx = Math.max(-1, dd.selectedIdx - 1);
          focusLink(dd);
          e.preventDefault();
          return;
      }
    });

    dd.toggles.forEach((t) => {
      t.addEventListener(pointerEvent, dd.toggleOpen);
      t.addEventListener('keydown', (e) => {
        const key = keyOf(e);
        if (!dd.open && (key === KEY.UP || key === KEY.DOWN)) {
          e.stopPropagation();
          return;
        }
        if (key === KEY.SPACE || key === KEY.ENTER) {
          dd.toggleByKey();
          e.stopPropagation();
          e.preventDefault();
        }
      });
    });

    dd.nav = el.closest('.w-nav');
    dropdowns.push(dd);
  }

  /* ===========================================================================
   * Navbar (.w-nav) — menu mobile/tablette
   * ======================================================================== */
  const TRAM_EASINGS = {
    'ease-in-quad': 'cubic-bezier(0.550, 0.085, 0.680, 0.530)',
    'ease-out-quad': 'cubic-bezier(0.250, 0.460, 0.450, 0.940)',
    'ease-in-out-quad': 'cubic-bezier(0.455, 0.030, 0.515, 0.955)',
    'ease-in-cubic': 'cubic-bezier(0.550, 0.055, 0.675, 0.190)',
    'ease-out-cubic': 'cubic-bezier(0.215, 0.610, 0.355, 1)',
    'ease-in-out-cubic': 'cubic-bezier(0.645, 0.045, 0.355, 1)',
    'ease-in-quart': 'cubic-bezier(0.895, 0.030, 0.685, 0.220)',
    'ease-out-quart': 'cubic-bezier(0.165, 0.840, 0.440, 1)',
    'ease-in-out-quart': 'cubic-bezier(0.770, 0, 0.175, 1)',
    'ease-in-quint': 'cubic-bezier(0.755, 0.050, 0.855, 0.060)',
    'ease-out-quint': 'cubic-bezier(0.230, 1, 0.320, 1)',
    'ease-in-out-quint': 'cubic-bezier(0.860, 0, 0.070, 1)',
    'ease-in-sine': 'cubic-bezier(0.470, 0, 0.745, 0.715)',
    'ease-out-sine': 'cubic-bezier(0.390, 0.575, 0.565, 1)',
    'ease-in-out-sine': 'cubic-bezier(0.445, 0.050, 0.550, 0.950)',
    'ease-in-expo': 'cubic-bezier(0.950, 0.050, 0.795, 0.035)',
    'ease-out-expo': 'cubic-bezier(0.190, 1, 0.220, 1)',
    'ease-in-out-expo': 'cubic-bezier(1, 0, 0, 1)',
    'ease-in-circ': 'cubic-bezier(0.600, 0.040, 0.980, 0.335)',
    'ease-out-circ': 'cubic-bezier(0.075, 0.820, 0.165, 1)',
    'ease-in-out-circ': 'cubic-bezier(0.785, 0.135, 0.150, 0.860)',
    'ease-in-back': 'cubic-bezier(0.600, -0.280, 0.735, 0.045)',
    'ease-out-back': 'cubic-bezier(0.175, 0.885, 0.320, 1.275)',
    'ease-in-out-back': 'cubic-bezier(0.680, -0.550, 0.265, 1.550)',
  };
  const navs = [];

  /* Animation du menu par transition CSS, comme la librairie « tram » de Webflow :
     la transition du transform s'ajoute à celle que le menu a déjà en CSS. */
  function animateMenu(nav, transition, from, to, done) {
    const menu = nav.menu;
    clearTimeout(nav.tweenTimer);
    if (nav.tweenEnd) menu.removeEventListener('transitionend', nav.tweenEnd);
    if (from != null) {
      menu.style.transition = nav.baseTransition;
      menu.style.transform = from;
      void menu.offsetWidth; // applique l'état de départ avant la transition
    }
    menu.style.transition = nav.baseTransition ? nav.baseTransition + ', ' + transition : transition;
    menu.style.transform = to;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(nav.tweenTimer);
      menu.removeEventListener('transitionend', nav.tweenEnd);
      nav.tweenEnd = null;
      done();
    };
    nav.tweenEnd = (e) => {
      if (e.target === menu && e.propertyName === 'transform') finish();
    };
    menu.addEventListener('transitionend', nav.tweenEnd);
    nav.tweenTimer = setTimeout(finish, nav.config.duration + 50);
  }

  function stopMenuTween(nav) {
    clearTimeout(nav.tweenTimer);
    if (nav.tweenEnd) nav.menu.removeEventListener('transitionend', nav.tweenEnd);
    nav.tweenEnd = null;
  }

  function overlayHeight(nav) {
    const c = nav.config;
    let h = c.docHeight
      ? Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight, doc.body.offsetHeight,
          doc.documentElement.offsetHeight, doc.documentElement.clientHeight)
      : height(doc.body);
    if (c.animOver) nav.menu.style.height = h + 'px';
    else if (getComputedStyle(nav.el).position !== 'fixed') h -= outerHeight(nav.el);
    if (nav.overlay) nav.overlay.style.height = h + 'px';
    return h;
  }

  function checkCollapse(nav) {
    const collapsed = getComputedStyle(nav.button).display !== 'none';
    if (nav.open && !collapsed) closeNav(nav, true);
    if (nav.open) overlayHeight(nav);
  }

  function openNav(nav, immediate) {
    if (nav.open) return;
    nav.open = true;
    nav.menu.setAttribute('data-nav-menu-open', '');
    nav.links.forEach((l) => l.classList.add('w--nav-link-open'));
    nav.dropdowns.forEach((d) => d.classList.add('w--nav-dropdown-open'));
    nav.dropdownToggles.forEach((d) => d.classList.add('w--nav-dropdown-toggle-open'));
    nav.dropdownLists.forEach((d) => d.classList.add('w--nav-dropdown-list-open'));
    nav.button.classList.add(OPEN);
    const c = nav.config;
    if (c.animation === 'none' || c.duration <= 0) immediate = true;
    const h = overlayHeight(nav);
    const menuHeight = outerHeight(nav.menu);
    const menuWidth = outerWidth(nav.menu);
    const navHeight = height(nav.el);
    checkCollapse(nav);
    emit(nav.el, 'COMPONENT_ACTIVE');
    doc.addEventListener('click', nav.onClickOutside);
    const done = () => nav.button.setAttribute('aria-expanded', 'true');
    if (immediate) return done();
    const transition = 'transform ' + c.duration + 'ms ' + c.easing;
    if (nav.overlay) {
      nav.menuPrev = nav.menu.previousElementSibling;
      nav.overlay.style.display = 'block';
      nav.overlay.appendChild(nav.menu);
    }
    if (c.animOver) {
      nav.menu.style.height = h + 'px';
      animateMenu(nav, transition, 'translateX(' + c.animDirect * menuWidth + 'px)', 'translateX(0px)', done);
      if (nav.overlay) nav.overlay.style.width = menuWidth + 'px';
      return;
    }
    animateMenu(nav, transition, 'translateY(' + -(navHeight + menuHeight) + 'px)', 'translateY(0px)', done);
  }

  function closeNav(nav, immediate) {
    if (!nav.open) return;
    nav.open = false;
    nav.button.classList.remove(OPEN);
    const c = nav.config;
    if (c.animation === 'none' || c.duration <= 0) immediate = true;
    emit(nav.el, 'COMPONENT_INACTIVE');
    doc.removeEventListener('click', nav.onClickOutside);

    const complete = () => {
      nav.menu.style.height = '';
      nav.menu.style.transition = nav.baseTransition;
      nav.menu.style.transform = 'translateY(0px) translateX(0px)';
      nav.menu.removeAttribute('data-nav-menu-open');
      nav.links.forEach((l) => l.classList.remove('w--nav-link-open'));
      nav.dropdowns.forEach((d) => d.classList.remove('w--nav-dropdown-open'));
      nav.dropdownToggles.forEach((d) => d.classList.remove('w--nav-dropdown-toggle-open'));
      nav.dropdownLists.forEach((d) => d.classList.remove('w--nav-dropdown-list-open'));
      if (nav.overlay && nav.overlay.children.length) {
        if (nav.menuPrev && nav.menuPrev.parentNode) nav.menuPrev.after(nav.menu);
        else nav.menuParent.prepend(nav.menu);
        nav.overlay.setAttribute('style', '');
        nav.overlay.style.display = 'none';
      }
      // Webflow déclenchait « w-close » sur la navbar : ses dropdowns se ferment.
      dropdowns.forEach((dd) => dd.nav === nav.el && dd.requestClose());
      nav.button.setAttribute('aria-expanded', 'false');
    };

    if (immediate) {
      stopMenuTween(nav);
      complete();
      return;
    }
    const transition = 'transform ' + c.duration + 'ms ' + c.easing2;
    if (c.animOver) {
      animateMenu(nav, transition, null, 'translateX(' + outerWidth(nav.menu) * c.animDirect + 'px)', complete);
      return;
    }
    animateMenu(nav, transition, null, 'translateY(' + -(height(nav.el) + outerHeight(nav.menu)) + 'px)', complete);
  }

  function focusNavLink(nav) {
    const link = nav.links[nav.selectedIdx];
    if (link) link.focus();
  }

  function setupNav(el, index) {
    const nav = { el, open: false, selectedIdx: -1 };
    nav.menu = el.querySelector('.w-nav-menu');
    nav.button = el.querySelector('.w-nav-button');
    if (!nav.menu || !nav.button) return;
    nav.links = Array.from(nav.menu.querySelectorAll('.w-nav-link'));
    nav.dropdowns = Array.from(nav.menu.querySelectorAll('.w-dropdown'));
    nav.dropdownToggles = Array.from(nav.menu.querySelectorAll('.w-dropdown-toggle'));
    nav.dropdownLists = Array.from(nav.menu.querySelectorAll('.w-dropdown-list'));
    const overlayId = 'w-nav-overlay-' + index;

    const animation = el.getAttribute('data-animation') || 'default';
    const easing = el.getAttribute('data-easing') || 'ease';
    const easing2 = el.getAttribute('data-easing2') || 'ease';
    const duration = el.getAttribute('data-duration');
    nav.config = {
      animation,
      animOver: /^over/.test(animation),
      animDirect: /left$/.test(animation) ? -1 : 1,
      easing: TRAM_EASINGS[easing] || easing,
      easing2: TRAM_EASINGS[easing2] || easing2,
      duration: duration != null ? Number(duration) : 400,
      docHeight: el.getAttribute('data-doc-height'),
    };
    const base = getComputedStyle(nav.menu).transition;
    nav.baseTransition = !base || /^all 0s ease 0s$/.test(base) ? '' : base;

    const brand = el.querySelector('.w-nav-brand');
    if (brand && brand.getAttribute('href') === '/' && brand.getAttribute('aria-label') == null) {
      el.querySelectorAll('.w-nav-brand').forEach((b) => b.setAttribute('aria-label', 'home'));
    }
    const btn = nav.button;
    btn.setAttribute('style', '-webkit-user-select: text;');
    if (btn.getAttribute('aria-label') == null) btn.setAttribute('aria-label', 'menu');
    btn.setAttribute('role', 'button');
    btn.setAttribute('tabindex', '0');
    btn.setAttribute('aria-controls', overlayId);
    btn.setAttribute('aria-haspopup', 'menu');
    btn.setAttribute('aria-expanded', 'false');

    nav.overlay = doc.createElement('div');
    nav.overlay.className = 'w-nav-overlay';
    nav.overlay.setAttribute('data-wf-ignore', '');
    nav.overlay.id = overlayId;
    el.appendChild(nav.overlay);
    nav.menuParent = nav.menu.parentElement;

    const toggle = deferred(() => (nav.open ? closeNav(nav) : openNav(nav)));
    nav.onClickOutside = deferred((e) => {
      if (!nav.open) return;
      const target = e.target instanceof Element ? e.target : null;
      if (!target || target.closest('.w-nav-menu') !== nav.menu) closeNav(nav);
    });

    btn.addEventListener('click', toggle);
    nav.menu.addEventListener('click', (e) => {
      const a = e.target instanceof Element ? e.target.closest('a') : null;
      if (!a || !nav.menu.contains(a)) return;
      if (!validClick(a)) {
        e.preventDefault();
        return;
      }
      const href = a.getAttribute('href');
      if (href && href.indexOf('#') === 0 && nav.open) closeNav(nav);
    });
    btn.addEventListener('keydown', (e) => {
      switch (keyOf(e)) {
        case KEY.SPACE:
        case KEY.ENTER:
          toggle();
          e.preventDefault();
          e.stopPropagation();
          return;
        case KEY.ESCAPE:
          closeNav(nav);
          e.preventDefault();
          e.stopPropagation();
          return;
        case KEY.RIGHT:
        case KEY.DOWN:
        case KEY.HOME:
        case KEY.END:
          if (nav.open) {
            nav.selectedIdx = keyOf(e) === KEY.END ? nav.links.length - 1 : 0;
            focusNavLink(nav);
          }
          e.preventDefault();
          e.stopPropagation();
      }
    });
    el.addEventListener('keydown', (e) => {
      if (!nav.open) return;
      nav.selectedIdx = nav.links.indexOf(doc.activeElement);
      switch (keyOf(e)) {
        case KEY.HOME:
        case KEY.END:
          nav.selectedIdx = keyOf(e) === KEY.END ? nav.links.length - 1 : 0;
          break;
        case KEY.ESCAPE:
          closeNav(nav);
          btn.focus();
          e.preventDefault();
          e.stopPropagation();
          return;
        case KEY.LEFT:
        case KEY.UP:
          nav.selectedIdx = Math.max(-1, nav.selectedIdx - 1);
          break;
        case KEY.RIGHT:
        case KEY.DOWN:
          nav.selectedIdx = Math.min(nav.links.length - 1, nav.selectedIdx + 1);
          break;
        default:
          return;
      }
      focusNavLink(nav);
      e.preventDefault();
      e.stopPropagation();
    });

    checkCollapse(nav);
    navs.push(nav);
  }

  // Webflow recalculait la navbar au resize, au changement d'orientation et au load.
  let resizeFrame = 0;
  const onResize = () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => navs.forEach(checkCollapse));
  };
  ['resize', 'orientationchange', 'load'].forEach((type) => window.addEventListener(type, onResize));

  /* ===========================================================================
   * Links : .w--current sur les liens de la page courante
   * ======================================================================== */
  function setupLinks() {
    const loc = window.location;
    const probe = doc.createElement('a');
    const spies = [];
    Array.from(doc.links).forEach((a) => {
      if (a.getAttribute('hreflang')) return;
      const raw = a.getAttribute('href');
      probe.href = raw;
      if (raw.indexOf(':') >= 0) return;
      if (probe.hash.length > 1 && probe.host + probe.pathname === loc.host + loc.pathname) {
        if (!/^#[a-zA-Z0-9\-_]+$/.test(probe.hash)) return;
        const section = doc.querySelector(probe.hash);
        if (section) spies.push({ link: a, section, active: false });
        return;
      }
      if (raw !== '#' && raw !== '') {
        const current =
          probe.href === loc.href || raw === loc.pathname || (/index\.(html|php)$/.test(raw) && /\/$/.test(loc.pathname));
        a.classList.toggle('w--current', current);
      }
    });
    if (!spies.length) return;
    const spy = () => {
      const top = window.scrollY;
      const vh = window.innerHeight;
      spies.forEach((s) => {
        if (s.link.getAttribute('hreflang')) return;
        const rect = s.section.getBoundingClientRect();
        const secTop = rect.top + top;
        const visible = !!(s.section.offsetWidth || s.section.offsetHeight || s.section.getClientRects().length);
        const active = visible && secTop + rect.height - 0.5 * vh >= top && secTop + 0.5 * vh <= top + vh;
        if (s.active !== active) {
          s.active = active;
          s.link.classList.toggle('w--current', active);
        }
      });
    };
    window.addEventListener('scroll', spy, { passive: true });
    spy();
  }

  /* ===========================================================================
   * Scroll : liens « # » neutralisés, défilement doux vers les ancres
   * ======================================================================== */
  function setupScroll() {
    const style = doc.createElement('style');
    style.textContent = '.wf-force-outline-none[tabindex="-1"]:focus{outline:none;}';
    doc.head.insertBefore(style, doc.head.firstChild);

    const inFrame = (() => {
      try {
        return !!window.frameElement;
      } catch (e) {
        return true;
      }
    })();
    const history = inFrame ? null : window.history;
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    const validHash = /^#[a-zA-Z0-9][\w:.-]*$/;

    const fixedHeaderHeight = () => {
      const header = doc.querySelector('header, body > .header, body > .w-nav:not([data-no-scroll])');
      return header && getComputedStyle(header).position === 'fixed' ? header.getBoundingClientRect().height : 0;
    };
    const targetTop = (target) => {
      const offset = fixedHeaderHeight();
      let top = target.getBoundingClientRect().top + window.scrollY - offset;
      if (target.getAttribute('data-scroll') === 'mid') {
        const available = window.innerHeight - offset;
        const h = target.getBoundingClientRect().height;
        if (h < available) top -= Math.round((available - h) / 2);
      }
      return top;
    };
    const duration = (target, from, to) => {
      if (doc.body.getAttribute('data-wf-scroll-motion') === 'none' || (reduceMotion && reduceMotion.matches)) return 0;
      let multiplier = 1;
      [doc.body, target].forEach((node) => {
        const t = parseFloat(node.getAttribute('data-scroll-time'));
        if (!isNaN(t) && t >= 0) multiplier = t;
      });
      return (472.143 * Math.log(Math.abs(from - to) + 125) - 2000) * multiplier;
    };
    const focusTarget = (target) => {
      const previous = target.getAttribute('tabindex');
      if (previous) target.setAttribute('data-wf-tabindex-swap', previous);
      else target.setAttribute('tabindex', '-1');
      target.classList.add('wf-force-outline-none');
      target.focus({ preventScroll: true });
      const swapped = target.getAttribute('data-wf-tabindex-swap');
      if (swapped) {
        target.setAttribute('tabindex', swapped);
        target.removeAttribute('data-wf-tabindex-swap');
      } else {
        target.removeAttribute('tabindex');
      }
      target.classList.remove('wf-force-outline-none');
    };
    const smoothScrollTo = (target) => {
      const from = window.scrollY;
      const to = targetTop(target);
      if (from === to) return;
      const total = duration(target, from, to);
      const start = Date.now();
      const step = () => {
        const elapsed = Date.now() - start;
        let y = to;
        if (elapsed <= total && total > 0) {
          const p = elapsed / total;
          y = from + (to - from) * (p < 0.5 ? 4 * p * p * p : (p - 1) * (2 * p - 2) * (2 * p - 2) + 1);
        }
        window.scroll(0, y);
        if (elapsed <= total && total > 0) requestAnimationFrame(step);
        else focusTarget(target);
      };
      requestAnimationFrame(step);
    };

    doc.addEventListener('click', (e) => {
      const a = e.target instanceof Element ? e.target.closest('a') : null;
      if (!a) return;
      const href = a.getAttribute('href');
      if (href === '#') {
        e.preventDefault();
        return;
      }
      if (!href || href.indexOf('#') < 0 || a.classList.contains('w-tab-link')) return;
      const loc = window.location;
      const hash = validHash.test(a.hash) && a.host + a.pathname === loc.host + loc.pathname ? a.hash : '';
      if (!hash) return;
      const target = doc.querySelector(hash);
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      if (loc.hash !== hash && history && history.pushState && (history.state && history.state.hash) !== hash) {
        history.pushState({ hash }, '', hash);
      }
      setTimeout(() => smoothScrollTo(target), 0);
    });
  }

  /* ===========================================================================
   * Démarrage (script chargé en defer, comme webflow.js : DOM déjà parsé)
   * ======================================================================== */
  setupLinks();
  setupScroll();
  doc.querySelectorAll('.w-dropdown').forEach(setupDropdown);
  doc.querySelectorAll('.w-nav').forEach(setupNav);
})();
